import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../../services/api';

const CANCEL_ALLOWED_ROLES = ['underwriter', 'admin'];
const POLICY_EDIT_ALLOWED_ROLES = ['underwriter', 'admin'];

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const parseAsUTC = (val) => {
    if (!val) return null;
    try {
        const s = String(val).trim();
        if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) {
            return new Date(s.replace(' ', 'T') + 'Z');
        }
        const d = new Date(s);
        return isNaN(d.getTime()) ? null : d;
    } catch {
        return null;
    }
};

const formatDate = (val, { timeZone = 'Africa/Nairobi' } = {}) => {
    const d = parseAsUTC(val);
    if (!d) return '—';
    try {
        return d.toLocaleString('en-KE', {
            timeZone,
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
    } catch {
        return String(val);
    }
};

const formatRelative = (val) => {
    const d = parseAsUTC(val);
    if (!d) return '—';
    const diff = Date.now() - d.getTime();
    if (diff < 0) return formatDate(val);
    if (diff < 60_000) return 'Just now';
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)}d ago`;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// ✅ FIXED: Defaults to KES and returns '—' for missing/zero values
const formatMoney = (val, currency = 'KES') => {
    if (val === null || val === undefined || val === '') return '—';
    const n = Number(val);
    if (!isFinite(n) || n === 0) return '—';
    return `${currency} ${n.toLocaleString()}`;
};

// =============================================================
// MAIN COMPONENT
// =============================================================
export default function CertificateDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);

    const canCancel = CANCEL_ALLOWED_ROLES.includes(user?.role);
    const canEditPolicy = POLICY_EDIT_ALLOWED_ROLES.includes(user?.role);

    // Data
    const [certData, setCertData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // PDF preview
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState('');

    // Download state
    const [downloading, setDownloading] = useState(false);
    const [downloaded, setDownloaded] = useState(false);

    // Verification
    const [verifying, setVerifying] = useState(false);
    const [verifyResult, setVerifyResult] = useState(null);

    // Copy feedback
    const [copied, setCopied] = useState(false);

    // Policy number modal
    const [showPolicyModal, setShowPolicyModal] = useState(false);
    const [policyInput, setPolicyInput] = useState('');
    const [savingPolicy, setSavingPolicy] = useState(false);
    const [policyError, setPolicyError] = useState('');
    const [policySuccess, setPolicySuccess] = useState('');

    // Cancel modal
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelReason, setCancelReason] = useState(1);
    const [cancelNotes, setCancelNotes] = useState('');
    const [cancelling, setCancelling] = useState(false);
    const [cancelError, setCancelError] = useState('');

    // Toast (page-level)
    const [toast, setToast] = useState(null); // { type: 'success' | 'error', message }

    // Refs
    const pdfObjectUrlRef = useRef(null);
    const isMountedRef = useRef(true);
    const policyInputRef = useRef(null);

    // ---------- Lifecycle ----------
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // ---------- Toast helper ----------
    const flash = useCallback((type, message, ttl = 4000) => {
        if (!isMountedRef.current) return;
        setToast({ type, message });
        setTimeout(() => {
            if (isMountedRef.current) setToast(null);
        }, ttl);
    }, []);

    // ---------- PDF preview ----------
    const loadPdfPreview = useCallback(async (certId, attempt = 1) => {
        const MAX_ATTEMPTS = 4;
        const BACKOFFS = [0, 1500, 3000, 5000];

        if (isMountedRef.current) {
            setPdfLoading(true);
            if (attempt === 1) setPdfError('');
        }

        try {
            const token = localStorage.getItem('token');
            const apiBase = process.env.REACT_APP_API_URL || '/api/v1';
            const res = await fetch(`${apiBase}/cargo/certificates/${certId}/download`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (!res.ok) {
                let errMsg = `Failed to load PDF (${res.status})`;
                let isRetryable = res.status === 404 || res.status === 502 || res.status === 503 || res.status === 202;
                try {
                    const errJson = await res.json();
                    errMsg = errJson.error || errMsg;
                    if (errJson.still_processing) isRetryable = true;
                    if (/processing|not available|still|being generated/i.test(errMsg)) {
                        isRetryable = true;
                    }
                } catch { /* ignore */ }

                if (isRetryable && attempt < MAX_ATTEMPTS) {
                    await new Promise((r) => setTimeout(r, BACKOFFS[attempt] || 5000));
                    if (!isMountedRef.current) return;
                    return loadPdfPreview(certId, attempt + 1);
                }
                throw new Error(errMsg);
            }

            const contentType = res.headers.get('content-type') || '';
            if (!contentType.includes('pdf')) {
                if (attempt < MAX_ATTEMPTS) {
                    await new Promise((r) => setTimeout(r, BACKOFFS[attempt] || 5000));
                    if (!isMountedRef.current) return;
                    return loadPdfPreview(certId, attempt + 1);
                }
                throw new Error('Server did not return a PDF');
            }

            const blob = await res.blob();
            const magic = new TextDecoder().decode(
                new Uint8Array(await blob.slice(0, 4).arrayBuffer())
            );
            if (magic !== '%PDF') {
                if (attempt < MAX_ATTEMPTS) {
                    await new Promise((r) => setTimeout(r, BACKOFFS[attempt] || 5000));
                    if (!isMountedRef.current) return;
                    return loadPdfPreview(certId, attempt + 1);
                }
                throw new Error('Invalid PDF file');
            }

            if (pdfObjectUrlRef.current) window.URL.revokeObjectURL(pdfObjectUrlRef.current);
            const objectUrl = window.URL.createObjectURL(blob);
            pdfObjectUrlRef.current = objectUrl;

            if (isMountedRef.current) {
                setPdfUrl(objectUrl);
                setPdfError('');
            }
        } catch (err) {
            if (isMountedRef.current) setPdfError(err.message || 'Could not load PDF preview.');
        } finally {
            if (isMountedRef.current) setPdfLoading(false);
        }
    }, []);

    // ---------- Load certificate ----------
    const loadCertificate = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await api.get(`/cargo/certificates/${id}`);
            if (!isMountedRef.current) return;
            setCertData(data);
            if (data?.id) loadPdfPreview(data.id);
        } catch (err) {
            if (!isMountedRef.current) return;
            setError(err.response?.data?.error || 'Failed to load certificate.');
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    }, [id, loadPdfPreview]);

    useEffect(() => {
        loadCertificate();
        return () => {
            if (pdfObjectUrlRef.current) {
                window.URL.revokeObjectURL(pdfObjectUrlRef.current);
                pdfObjectUrlRef.current = null;
            }
        };
    }, [loadCertificate]);

    // ---------- Download ----------
    const download = useCallback(async () => {
        if (!certData) return;
        setDownloading(true);
        try {
            const token = localStorage.getItem('token');
            const apiBase = process.env.REACT_APP_API_URL || '/api/v1';
            const res = await fetch(`${apiBase}/cargo/certificates/${certData.id}/download`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) throw new Error('Download failed');

            const blob = await res.blob();
            const objectUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            const suffix = certData.cancelled_at ? '-CANCELLED' : '';
            a.download = `${certData.certificateNo || 'certificate'}${suffix}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(objectUrl);
            setDownloaded(true);
        } catch (err) {
            flash('error', err.message || 'Could not download certificate.');
        } finally {
            if (isMountedRef.current) setDownloading(false);
        }
    }, [certData, flash]);

    // ---------- Verify ----------
    const verifyCertificate = useCallback(async () => {
        if (!certData?.certificateNo) return;
        setVerifying(true);
        setVerifyResult(null);
        try {
            const { data } = await api.post('/cargo/certificates/verify', {
                certificate_number: certData.certificateNo,
            });
            if (isMountedRef.current) setVerifyResult(data);
        } catch (err) {
            if (isMountedRef.current) {
                setVerifyResult({
                    error: err.response?.data?.error || 'Verification failed',
                });
            }
        } finally {
            if (isMountedRef.current) setVerifying(false);
        }
    }, [certData]);

    // ---------- Copy ----------
    const copyCertNumber = useCallback(async () => {
        if (!certData?.certificateNo) return;
        try {
            await navigator.clipboard.writeText(certData.certificateNo);
            setCopied(true);
            setTimeout(() => {
                if (isMountedRef.current) setCopied(false);
            }, 1500);
        } catch {
            flash('error', 'Could not copy to clipboard.');
        }
    }, [certData, flash]);

    // ---------- Policy number ----------
    const openPolicyModal = useCallback(() => {
        setPolicyInput(certData?.iraPolicyNum || '');
        setPolicyError('');
        setPolicySuccess('');
        setShowPolicyModal(true);
    }, [certData]);

    const submitPolicyNumber = useCallback(async () => {
        const trimmed = String(policyInput || '').trim();
        if (!trimmed) {
            setPolicyError('Policy number is required.');
            return;
        }
        if (trimmed.length > 100) {
            setPolicyError('Policy number cannot exceed 100 characters.');
            return;
        }
        setSavingPolicy(true);
        setPolicyError('');
        try {
            const { data } = await api.put(
                `/cargo/certificates/${certData.id}/policy-number`,
                { policy_number: trimmed }
            );
            if (!isMountedRef.current) return;
            setCertData((prev) => ({
                ...prev,
                iraPolicyNum: data.policy_number || trimmed,
            }));
            setPolicySuccess('Policy number updated successfully.');
            flash('success', 'Policy number saved.');
            setTimeout(() => {
                if (isMountedRef.current) {
                    setShowPolicyModal(false);
                    setPolicySuccess('');
                }
            }, 700);
        } catch (err) {
            setPolicyError(
                err.response?.data?.error ||
                err.response?.data?.details ||
                'Could not save policy number.'
            );
        } finally {
            if (isMountedRef.current) setSavingPolicy(false);
        }
    }, [policyInput, certData, flash]);

    // ---------- Cancel ----------
    const submitCancel = useCallback(async () => {
        if (!certData) return;
        setCancelling(true);
        setCancelError('');
        try {
            await api.post('/cargo/certificates/cancel', {
                certificate_number: certData.certificateNo,
                cancel_reason: Number(cancelReason),
                notes: cancelNotes || undefined,
            });
            if (!isMountedRef.current) return;
            setShowCancelModal(false);
            flash('success', 'Certificate cancelled successfully.');
            await loadCertificate();
        } catch (err) {
            setCancelError(
                err.response?.data?.error ||
                err.response?.data?.details ||
                'Could not cancel certificate.'
            );
        } finally {
            if (isMountedRef.current) setCancelling(false);
        }
    }, [certData, cancelReason, cancelNotes, flash, loadCertificate]);

    // ---------- Derived ----------
    const isCancelled = !!certData?.cancelled_at;
    const headerAccent = isCancelled
        ? 'from-red-600 via-red-500 to-red-600'
        : 'from-[#2d5232] via-[#3a663f] to-[#4d8c54]';

    const auditSteps = useMemo(() => {
        if (!certData) return [];
        const steps = [
            {
                key: 'issued',
                label: 'Issued',
                timestamp: certData.certificateDate || certData.created_at,
                actor: certData.issued_by_name || 'System',
                ref: certData.transactionNo || null,
                color: 'green',
            },
        ];
        if (isCancelled) {
            steps.push({
                key: 'cancelled',
                label: 'Cancelled',
                timestamp: certData.cancelled_at,
                actor: certData.cancelled_by_name || 'System',
                ref: certData.cancel_reason ? `#${certData.cancel_reason}` : null,
                color: 'red',
            });
        }
        return steps;
    }, [certData, isCancelled]);

    // =========================================================
    // RENDER — Loading
    // =========================================================
    if (loading) {
        return (
            <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6 animate-fadeIn">
                <div className="animate-pulse space-y-6">
                    <div className="h-4 w-40 bg-gray-200 dark:bg-slate-700 rounded" />
                    <div className="h-48 bg-gray-100 dark:bg-slate-800 rounded-2xl" />
                    <div className="h-64 bg-gray-100 dark:bg-slate-800 rounded-2xl" />
                </div>
            </div>
        );
    }

    // =========================================================
    // RENDER — Error
    // =========================================================
    if (error) {
        return (
            <div className="max-w-2xl mx-auto p-8 text-center animate-fadeIn">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                    <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
                    Could Not Load Certificate
                </h3>
                <p className="text-gray-500 dark:text-slate-400 mb-6">{error}</p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                    <button
                        onClick={() => navigate('/cargo/certificates')}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 font-semibold hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to Certificates
                    </button>
                    <button
                        onClick={loadCertificate}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#3a663f] hover:bg-[#2d5232] text-white font-semibold transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    // =========================================================
    // RENDER — Main
    // =========================================================
    return (
        <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6 animate-fadeIn">

            {/* ─── Back link ─────────────────────────────────── */}
            <button
                onClick={() => navigate('/cargo/certificates')}
                className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400 hover:text-[#3a663f] dark:hover:text-green-400 font-medium transition-colors"
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Certificates
            </button>

            {/* ─── Toast ─────────────────────────────────────── */}
            {toast && (
                <div
                    className={`rounded-2xl border-2 px-4 py-3 flex items-start gap-3 shadow-lg animate-slideDown ${toast.type === 'success'
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
                        : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
                        }`}
                    role="alert"
                >
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {toast.type === 'success' ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        )}
                    </svg>
                    <p className="text-sm flex-1">{toast.message}</p>
                    <button
                        onClick={() => setToast(null)}
                        aria-label="Dismiss"
                        className="text-current opacity-60 hover:opacity-100 transition-opacity"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            )}

            {/* ─── HERO ──────────────────────────────────────── */}
            <div
                className={`relative overflow-hidden rounded-3xl shadow-2xl border-2 ${isCancelled
                    ? 'border-red-200 dark:border-red-800'
                    : 'border-gray-100 dark:border-slate-700'
                    }`}
            >
                <div className={`relative p-6 sm:p-8 text-white bg-gradient-to-r ${headerAccent}`}>
                    {/* Decorative blobs */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

                    <div className="relative flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                        {/* Left: identity */}
                        <div className="min-w-0">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <span className="text-xs font-semibold uppercase tracking-widest text-white/70">
                                    IRA Marine Cargo Certificate
                                </span>
                            </div>

                            <div className="flex items-center gap-3 flex-wrap">
                                <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight break-all">
                                    {certData.certificateNo || '—'}
                                </h1>
                                {certData.certificateNo && (
                                    <button
                                        onClick={copyCertNumber}
                                        aria-label="Copy certificate number"
                                        title="Copy certificate number"
                                        className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                                    >
                                        {copied ? (
                                            <svg className="w-4 h-4 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                            </svg>
                                        ) : (
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                        )}
                                    </button>
                                )}
                            </div>

                            <p className="text-white/70 text-sm mt-2">
                                Issued {formatRelative(certData.certificateDate || certData.created_at)}
                            </p>
                        </div>

                        {/* Right: status + KPIs */}
                        <div className="flex flex-col items-start lg:items-end gap-3">
                            <span
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide ${isCancelled
                                    ? 'bg-red-500/30 text-white'
                                    : 'bg-white/20 text-white'
                                    }`}
                            >
                                {isCancelled ? (
                                    <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                        Cancelled
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Active
                                    </>
                                )}
                            </span>

                            <div className="text-left lg:text-right">
                                <p className="text-xs uppercase tracking-wide text-white/60 font-medium">
                                    Insured Value
                                </p>
                                <p className="text-2xl sm:text-3xl font-extrabold">
                                    {/* ✅ FIXED: Explicitly pass 'KES' */}
                                    {formatMoney(certData.insuredAmount, 'KES')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ─── DETAILS + AUDIT (two-column on desktop) ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Details — 2 columns */}
                <div className="lg:col-span-2 space-y-4">
                    {/* Identity block */}
                    <div className="bg-white dark:bg-slate-800 rounded-2xl border-2 border-gray-100 dark:border-slate-700 p-6 shadow-sm">
                        <div className="flex items-center gap-2 mb-5">
                            <svg className="w-5 h-5 text-[#3a663f] dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0" />
                            </svg>
                            <h2 className="text-sm font-bold text-gray-700 dark:text-slate-200 uppercase tracking-wider">
                                Certificate Details
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <InfoRow label="IDF Number" value={certData.idf_number} mono />
                            <InfoRow label="Quote Number" value={certData.quote_number} mono />
                            <InfoRow label="Insurer" value={certData.insurerDetails?.name || 'Mayfair Insurance'} />
                            <InfoRow label="Issued By" value={certData.issued_by_name || 'System'} />
                            {certData.transactionNo && (
                                <InfoRow label="DMVIC Reference" value={certData.transactionNo} mono />
                            )}
                            <InfoRow
                                label="Issue Date (EAT)"
                                value={formatDate(certData.certificateDate)}
                            />
                        </div>

                        {/* Policy number — highlighted */}
                        <div className="mt-6 pt-6 border-t border-gray-100 dark:border-slate-700">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-10 h-10 rounded-xl bg-[#3a663f]/10 dark:bg-[#3a663f]/20 flex items-center justify-center flex-shrink-0">
                                        <svg className="w-5 h-5 text-[#3a663f] dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                        </svg>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-slate-500">
                                            Policy Number
                                        </p>
                                        <p
                                            className={`text-base font-mono font-bold truncate ${certData.iraPolicyNum
                                                ? 'text-[#3a663f] dark:text-green-400'
                                                : 'text-gray-400 dark:text-slate-500 italic font-normal'
                                                }`}
                                        >
                                            {certData.iraPolicyNum || 'Not set'}
                                        </p>
                                    </div>
                                </div>

                                {canEditPolicy && (
                                    <button
                                        onClick={openPolicyModal}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border-2 border-[#3a663f] text-[#3a663f] dark:border-green-400 dark:text-green-400 hover:bg-[#3a663f] hover:text-white dark:hover:bg-green-400 dark:hover:text-slate-900 transition-all"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        {certData.iraPolicyNum ? 'Edit' : 'Add'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Financials */}
                    <div className="bg-white dark:bg-slate-800 rounded-2xl border-2 border-gray-100 dark:border-slate-700 p-6 shadow-sm">
                        <div className="flex items-center gap-2 mb-5">
                            <svg className="w-5 h-5 text-[#3a663f] dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h2 className="text-sm font-bold text-gray-700 dark:text-slate-200 uppercase tracking-wider">
                                Financials
                            </h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            {/* ✅ FIXED: Explicitly pass 'KES' */}
                            <InfoRow label="Insured Amount" value={formatMoney(certData.insuredAmount, 'KES')} />
                            <InfoRow
                                label="Total Charges"
                                value={formatMoney(certData.insurerDetails?.totalCharges, 'KES')}
                            />
                        </div>
                    </div>
                </div>

                {/* Audit timeline — 1 column */}
                <div className="lg:col-span-1">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl border-2 border-gray-100 dark:border-slate-700 p-6 shadow-sm h-full">
                        <div className="flex items-center gap-2 mb-5">
                            <svg className="w-5 h-5 text-[#3a663f] dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h2 className="text-sm font-bold text-gray-700 dark:text-slate-200 uppercase tracking-wider">
                                Audit Trail
                            </h2>
                        </div>

                        <ol className="relative space-y-5">
                            {auditSteps.map((step, idx) => {
                                const isLast = idx === auditSteps.length - 1;
                                const dotColor =
                                    step.color === 'green'
                                        ? 'bg-green-500 shadow-green-500/40'
                                        : 'bg-red-500 shadow-red-500/40';
                                return (
                                    <li key={step.key} className="relative flex gap-4">
                                        {/* Rail */}
                                        {!isLast && (
                                            <div className="absolute left-[15px] top-8 bottom-[-20px] w-0.5 bg-gray-200 dark:bg-slate-700" />
                                        )}
                                        {/* Dot */}
                                        <div className={`relative z-10 w-8 h-8 rounded-full ${dotColor} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                                            {step.color === 'green' ? (
                                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                                </svg>
                                            ) : (
                                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            )}
                                        </div>
                                        {/* Body */}
                                        <div className="flex-1 min-w-0 pt-1">
                                            <div className="flex flex-wrap items-baseline gap-2">
                                                <p className="text-sm font-bold text-gray-800 dark:text-white">
                                                    {step.label}
                                                </p>
                                                <p
                                                    className="text-xs text-gray-400 dark:text-slate-500"
                                                    title={formatDate(step.timestamp)}
                                                >
                                                    {formatRelative(step.timestamp)}
                                                </p>
                                            </div>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                                                By{' '}
                                                <strong className="text-gray-700 dark:text-slate-300">
                                                    {step.actor}
                                                </strong>
                                            </p>
                                            {step.ref && (
                                                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1 font-mono">
                                                    Ref: {step.ref}
                                                </p>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    </div>
                </div>
            </div>

            {/* ─── ACTIONS ───────────────────────────────────── */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border-2 border-gray-100 dark:border-slate-700 p-5 shadow-sm">
                <div className="flex flex-col sm:flex-row gap-3">
                    <button
                        onClick={download}
                        disabled={downloading}
                        className={`flex-1 inline-flex items-center justify-center gap-2 py-3 px-5 rounded-xl font-bold transition-all disabled:opacity-60 disabled:cursor-not-allowed ${isCancelled
                            ? 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600'
                            : 'bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white shadow-lg shadow-[#3a663f]/20 hover:shadow-xl'
                            }`}
                    >
                        {downloading ? (
                            <>
                                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Downloading...
                            </>
                        ) : (
                            <>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                {downloaded ? 'Download Again' : isCancelled ? 'Download Cancelled PDF' : 'Download Certificate'}
                            </>
                        )}
                    </button>

                    <button
                        onClick={verifyCertificate}
                        disabled={verifying}
                        className="flex-1 inline-flex items-center justify-center gap-2 py-3 px-5 rounded-xl font-bold bg-white dark:bg-slate-800 border-2 border-[#3a663f] dark:border-green-400 text-[#3a663f] dark:text-green-400 hover:bg-[#3a663f] hover:text-white dark:hover:bg-green-400 dark:hover:text-slate-900 transition-all disabled:opacity-60"
                    >
                        {verifying ? (
                            <>
                                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Verifying...
                            </>
                        ) : (
                            <>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Verify Certificate
                            </>
                        )}
                    </button>

                    {canCancel && !isCancelled && (
                        <button
                            onClick={() => {
                                setCancelError('');
                                setCancelNotes('');
                                setCancelReason(1);
                                setShowCancelModal(true);
                            }}
                            className="flex-1 inline-flex items-center justify-center gap-2 py-3 px-5 rounded-xl font-bold bg-white dark:bg-slate-800 border-2 border-red-500 text-red-600 dark:text-red-400 hover:bg-red-500 hover:text-white dark:hover:bg-red-500 transition-all"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Cancel Certificate
                        </button>
                    )}
                </div>

                {!canCancel && !isCancelled && (
                    <p className="text-xs text-gray-500 dark:text-slate-400 pt-3 text-center">
                        Only an <strong>underwriter</strong> or <strong>admin</strong> can cancel this certificate.
                    </p>
                )}
            </div>

            {/* ─── VERIFY RESULT ─────────────────────────────── */}
            {verifyResult && (
                <div
                    className={`rounded-2xl border-2 p-5 flex items-start gap-3 shadow-lg animate-slideDown ${verifyResult.error
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
                        : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
                        }`}
                >
                    <svg className="w-6 h-6 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {verifyResult.error ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        )}
                    </svg>
                    <div className="flex-1 min-w-0">
                        <p className="font-bold">
                            {verifyResult.error ? 'Verification Failed' : 'Verification Successful'}
                        </p>
                        <p className="text-sm mt-0.5 break-words">
                            {verifyResult.error ||
                                verifyResult.message ||
                                verifyResult.statusMessage ||
                                'Certificate is authentic and valid.'}
                        </p>
                    </div>
                    <button
                        onClick={() => setVerifyResult(null)}
                        aria-label="Dismiss"
                        className="text-current opacity-60 hover:opacity-100 transition-opacity"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            )}

            {/* ─── PDF PREVIEW ───────────────────────────────── */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border-2 border-gray-100 dark:border-slate-700 overflow-hidden shadow-sm">
                <div className="px-5 py-4 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-[#3a663f] dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <h2 className="text-sm font-bold text-gray-700 dark:text-slate-200 uppercase tracking-wider">
                            Certificate Preview
                        </h2>
                    </div>
                    {pdfUrl && !pdfLoading && !pdfError && (
                        <div className="flex items-center gap-2">
                            <a
                                href={pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-[#3a663f] dark:text-green-400 hover:underline font-semibold"
                            >
                                Open in new tab
                            </a>
                            <span className="text-gray-300 dark:text-slate-600">·</span>
                            <button
                                onClick={() => loadPdfPreview(certData.id)}
                                className="text-xs text-[#3a663f] dark:text-green-400 hover:underline font-semibold"
                            >
                                Reload
                            </button>
                        </div>
                    )}
                </div>

                {pdfLoading && (
                    <div className="py-20 flex flex-col items-center justify-center gap-3">
                        <svg className="animate-spin h-10 w-10 text-[#3a663f]" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        <p className="text-sm text-gray-500 dark:text-slate-400">Loading PDF preview...</p>
                    </div>
                )}

                {pdfError && !pdfLoading && (
                    <div className="py-16 text-center px-6">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                            <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <p className="text-sm text-red-600 dark:text-red-400 mb-4">{pdfError}</p>
                        <button
                            onClick={() => loadPdfPreview(certData.id)}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#3a663f] hover:bg-[#2d5232] text-white font-semibold text-sm transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                )}

                {pdfUrl && !pdfLoading && !pdfError && (
                    <object
                        data={pdfUrl}
                        type="application/pdf"
                        className="w-full bg-gray-50 dark:bg-slate-900"
                        style={{ height: '720px' }}
                    >
                        <div className="p-10 text-center">
                            <p className="text-sm text-gray-500 dark:text-slate-400 mb-3">
                                Your browser can&apos;t display PDFs inline.
                            </p>
                            <a
                                href={pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 bg-[#3a663f] hover:bg-[#2d5232] text-white text-sm font-semibold py-2.5 px-5 rounded-xl transition-colors"
                            >
                                Open PDF in new tab
                            </a>
                        </div>
                    </object>
                )}
            </div>

            {/* ─── POLICY NUMBER MODAL ───────────────────────── */}
            {showPolicyModal && (
                <Modal onClose={() => setShowPolicyModal(false)} titleId="policy-modal-title">
                    <div className="p-6">
                        <div className="flex items-start gap-3 mb-5">
                            <div className="w-12 h-12 rounded-2xl bg-[#3a663f]/10 dark:bg-[#3a663f]/20 flex items-center justify-center flex-shrink-0">
                                <svg className="w-6 h-6 text-[#3a663f] dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                </svg>
                            </div>
                            <div>
                                <h3 id="policy-modal-title" className="text-lg font-bold text-gray-900 dark:text-white">
                                    {certData?.iraPolicyNum ? 'Update Policy Number' : 'Add Policy Number'}
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
                                    For certificate{' '}
                                    <span className="font-mono font-semibold text-gray-700 dark:text-slate-200">
                                        {certData?.certificateNo}
                                    </span>
                                </p>
                            </div>
                        </div>

                        <label
                            htmlFor="policy-number-input"
                            className="block text-xs uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1.5 font-semibold"
                        >
                            Policy Number
                        </label>
                        <input
                            id="policy-number-input"
                            ref={policyInputRef}
                            type="text"
                            autoFocus
                            value={policyInput}
                            onChange={(e) => setPolicyInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !savingPolicy && policyInput.trim()) {
                                    submitPolicyNumber();
                                }
                            }}
                            maxLength={100}
                            placeholder="e.g., MAY/CARGO/2026/000123"
                            className="w-full rounded-xl border-2 border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-800 dark:text-white px-4 py-3 mb-1 font-mono focus:ring-2 focus:ring-[#3a663f] focus:border-[#3a663f] outline-none transition-all"
                        />
                        <p className="text-xs text-gray-400 dark:text-slate-500 text-right mb-3">
                            {policyInput.length}/100
                        </p>

                        {policyError && (
                            <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm px-3 py-2 mb-3 flex items-start gap-2">
                                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {policyError}
                            </div>
                        )}

                        {policySuccess && (
                            <div className="rounded-xl bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 text-sm px-3 py-2 mb-3 flex items-start gap-2">
                                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {policySuccess}
                            </div>
                        )}

                        <div className="flex gap-3 pt-2">
                            <button
                                onClick={() => setShowPolicyModal(false)}
                                disabled={savingPolicy}
                                className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 font-semibold hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-60"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitPolicyNumber}
                                disabled={savingPolicy || !policyInput.trim()}
                                className="flex-1 py-2.5 rounded-xl bg-[#3a663f] hover:bg-[#2d5232] text-white font-bold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                            >
                                {savingPolicy ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                        Saving...
                                    </>
                                ) : (
                                    'Save'
                                )}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* ─── CANCEL MODAL ──────────────────────────────── */}
            {showCancelModal && (
                <Modal onClose={() => setShowCancelModal(false)} titleId="cancel-modal-title">
                    <div className="p-6">
                        <div className="flex items-start gap-3 mb-5">
                            <div className="w-12 h-12 rounded-2xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div>
                                <h3 id="cancel-modal-title" className="text-lg font-bold text-gray-900 dark:text-white">
                                    Cancel this certificate?
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                                    Certificate{' '}
                                    <span className="font-mono font-semibold text-gray-700 dark:text-slate-200">
                                        {certData.certificateNo}
                                    </span>{' '}
                                    will be marked as cancelled on DMVIC. This action cannot be undone.
                                </p>
                            </div>
                        </div>

                        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 mb-4 text-xs text-amber-800 dark:text-amber-300">
                            A new certificate can be issued afterwards for the same quote.
                        </div>

                        <label
                            htmlFor="cancel-reason"
                            className="block text-xs uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1.5 font-semibold"
                        >
                            Reason
                        </label>
                        <select
                            id="cancel-reason"
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            className="w-full rounded-xl border-2 border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-800 dark:text-white px-4 py-3 mb-4 focus:ring-2 focus:ring-[#3a663f] focus:border-[#3a663f] outline-none transition-all"
                        >
                            <option value={1}>Reason 1 — Client request</option>
                            <option value={2}>Reason 2 — Duplicate issuance</option>
                            <option value={3}>Reason 3 — Data correction</option>
                            <option value={4}>Reason 4 — Payment reversal</option>
                            <option value={5}>Reason 5 — Other</option>
                        </select>

                        <label
                            htmlFor="cancel-notes"
                            className="block text-xs uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1.5 font-semibold"
                        >
                            Notes (optional)
                        </label>
                        <textarea
                            id="cancel-notes"
                            value={cancelNotes}
                            onChange={(e) => setCancelNotes(e.target.value)}
                            rows={3}
                            maxLength={500}
                            placeholder="Add any context for the audit log..."
                            className="w-full rounded-xl border-2 border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-800 dark:text-white px-4 py-3 mb-1 focus:ring-2 focus:ring-[#3a663f] focus:border-[#3a663f] outline-none transition-all resize-none"
                        />
                        <p className="text-xs text-gray-400 dark:text-slate-500 text-right mb-4">
                            {cancelNotes.length}/500
                        </p>

                        {cancelError && (
                            <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm px-3 py-2 mb-4 flex items-start gap-2">
                                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {cancelError}
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowCancelModal(false)}
                                disabled={cancelling}
                                className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 font-semibold hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-60"
                            >
                                Keep Certificate
                            </button>
                            <button
                                onClick={submitCancel}
                                disabled={cancelling}
                                className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                            >
                                {cancelling ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                        Cancelling...
                                    </>
                                ) : (
                                    'Yes, Cancel It'
                                )}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* ─── Animations ────────────────────────────────── */}
            <style>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideDown { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
                .animate-fadeIn { animation: fadeIn 0.25s ease-out; }
                .animate-slideDown { animation: slideDown 0.2s ease-out; }
            `}</style>
        </div>
    );
}

// =============================================================
// SUB-COMPONENTS
// =============================================================

function InfoRow({ label, value, mono = false }) {
    return (
        <div>
            <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-1 font-bold">
                {label}
            </p>
            <p
                className={`text-sm font-semibold text-gray-800 dark:text-white break-words ${mono ? 'font-mono' : ''
                    }`}
            >
                {value || '—'}
            </p>
        </div>
    );
}

function Modal({ children, onClose, titleId }) {
    const containerRef = useRef(null);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);

        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [onClose]);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
        >
            <div
                ref={containerRef}
                className="bg-white dark:bg-slate-800 rounded-3xl max-w-md w-full shadow-2xl overflow-hidden animate-slideDown"
            >
                {children}
            </div>
        </div>
    );
}